<template>
  <div class="w-full flex flex-col">
    <div class="mb-2.5">
      <p class="text-sm text-[#58595b] mb-2.5 text-center">Calculate the electrical power, flow rate or temperature requirement as follows:</p>
      <ul class="text-sm mb-2.5 flex flex-col gap-1">
        <li>SCFM = airflow in standard cubic feet per minute</li>
        <li>ΔT = temperature rise in degrees F from the inlet to the exhaust</li>
        <li>Watts = SCFM x ΔT/3</li>
      </ul>
      <div class="font-bold text-center">Fill in two values to determine the third</div>
    </div>
    <div class="w-full flex flex-col gap-1">
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">SCFM</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.scfm}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.scfm}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.scfm}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.scfm) }"
            v-model="state.scfm"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.scfm, state.scfm)" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">ΔT °F</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.tempChange}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.tempChange}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.tempChange}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.tempChange) }"
            v-model="state.tempChange"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.tempChange, state.tempChange)" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">Watts</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.watts}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.watts}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.watts}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.watts) }"
            v-model="state.watts"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.watts, state.watts)" />
        </label>
      </div>
      <div class="w-full flex items-center justify-center px-4 gap-2.5 mt-1">
        <div
          class="text-sm py-1.5 px-3 rounded text-white bg-red hover:bg-dark-red cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': state.inputFields.length === 0 }"
          @click="resetAction">
          Reset
        </div>
        <div
          class="w-full max-w-44 text-sm py-1.5 px-3 rounded text-white bg-red hover:bg-dark-red cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': state.inputFields.length < minInputFieldsQty }"
          @click="calculateAction">
          Calculate
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive } from 'vue';
import CalculatorsHelper from '@/helpers/global/calculators.helper';
import GlobalHelper from '@/helpers/global/global.helper';

enum CalculatorFieldsTypes {
  scfm = 'scfm',
  tempChange = 'temp-change',
  watts = 'watts',
}

const state = reactive({
  scfm: '' as string | number,
  tempChange: '' as string | number,
  watts: '' as string | number,
  inputFields: [] as string[],
  outputFields: [] as string[],
});

const calculatorFieldsList: CalculatorFieldsTypes[] = [CalculatorFieldsTypes.scfm, CalculatorFieldsTypes.tempChange, CalculatorFieldsTypes.watts];
const calcType: string = 'power-flowrate-temp-calculator';
const minInputFieldsQty: number = 2;

const updateInputAndOutputFields = (field: CalculatorFieldsTypes, fieldValue: string | number) => {
  CalculatorsHelper.generateInputAndOutputFields(state.inputFields, state.outputFields, field, fieldValue, calcType, minInputFieldsQty);
  state.inputFields = CalculatorsHelper.getInputFields();
  state.outputFields = CalculatorsHelper.getOutputFields();
};

const setOutputFields = () => {
  calculatorFieldsList.forEach((field) => {
    state.outputFields.push(field);
  });
};

const resetAction = () => {
  state.scfm = '';
  state.tempChange = '';
  state.watts = '';
  state.inputFields = [];
  state.outputFields = [];

  setOutputFields();
};

const calculateAction = () => {
  if (state.inputFields.length === minInputFieldsQty) {
    const scfmValue = Number(state.scfm);
    const tempChangeValue = Number(state.tempChange);
    const wattsValue = Number(state.watts);

    const firstOutputField = state.outputFields[0];

    switch (firstOutputField) {
      case CalculatorFieldsTypes.scfm:
        state.scfm = GlobalHelper.roundToDecimals(wattsValue / (tempChangeValue / 3), 4);
        break;
      case CalculatorFieldsTypes.tempChange:
        state.tempChange = GlobalHelper.roundToDecimals(3 * (wattsValue / scfmValue), 4);
        break;
      case CalculatorFieldsTypes.watts:
        state.watts = GlobalHelper.roundToDecimals(scfmValue * (tempChangeValue / 3), 4);
        break;
    }
  }
};

onMounted(() => {
  setOutputFields();
});
</script>
